import React, { useState, useEffect, useCallback, useRef } from "react";
import { jwtDecode } from "jwt-decode";

export default function SessionTimer() {
  const [timeLeft, setTimeLeft] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const dismissedRef = useRef(false);
  const loggedOutRef = useRef(false);

  const forceLogout = useCallback(() => {
    if (loggedOutRef.current) return;
    loggedOutRef.current = true;
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  }, []);

  const checkToken = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setTimeLeft(null);
      setShowWarning(false);
      dismissedRef.current = false;
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const expMs = decoded.exp * 1000;
      const nowMs = Date.now();
      const remaining = expMs - nowMs;

      if (remaining <= 0) {
        forceLogout();
        return;
      }

      setTimeLeft(remaining);

      if (remaining < 5 * 60 * 1000) {
        setShowWarning(true);
        dismissedRef.current = false;
      } else if (remaining < 15 * 60 * 1000) {
        setShowWarning(!dismissedRef.current);
      } else {
        setShowWarning(false);
        dismissedRef.current = false;
      }
    } catch {
      setTimeLeft(null);
      setShowWarning(false);
    }
  }, [forceLogout]);

  useEffect(() => {
    checkToken();
    const interval = setInterval(checkToken, 30 * 1000);
    window.addEventListener("storage", checkToken);
    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", checkToken);
    };
  }, [checkToken]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      dismissedRef.current = true;
      setShowWarning(false);
      setIsExiting(false);
    }, 250);
  };

  if (!showWarning || timeLeft === null) return null;

  const minutes = Math.floor(timeLeft / 60000);
  const seconds = Math.floor((timeLeft % 60000) / 1000);

  return (
    <>
      <style>{sessionTimerCSS}</style>
      <div
        className={`session-timer ${isExiting ? "session-timer-exit" : ""} ${timeLeft < 60 * 1000 ? "session-critical" : "session-warning"}`}
        role="alert"
      >
        <span className="session-timer-text">
          {timeLeft < 60 * 1000
            ? `Session expires in ${seconds}s \u2014 save your work`
            : `Session expires in ${minutes}m ${seconds}s`}
        </span>
        <button
          className="session-timer-close"
          onClick={handleDismiss}
          aria-label="Dismiss session warning"
        >
          ×
        </button>
      </div>
    </>
  );
}

const sessionTimerCSS = `
.session-timer {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 40px 6px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: var(--font-sans);
  color: #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
  border-bottom: 1px solid rgba(0,0,0,0.08);
  animation: stFadeIn 0.2s ease-out;
}

.session-warning {
  background: linear-gradient(135deg, #F59E0B, #D97706);
}

.session-critical {
  background: linear-gradient(135deg, #DC2626, #B91C1C);
}

.session-timer-exit {
  animation: stFadeOut 0.2s ease forwards;
}

.session-timer-text {
  flex: 1;
  text-align: center;
}

.session-timer-close {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  padding: 4px 10px;
  opacity: 0.8;
  border-radius: 4px;
  transition: opacity 0.15s, background 0.15s;
  font-family: var(--font-sans);
}

.session-timer-close:hover {
  opacity: 1;
  background: rgba(255,255,255,0.15);
}

@keyframes stFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes stFadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}
`;
